import React, { useEffect, useState } from "react";
import {
    doc,
    getDoc,
    setDoc,
    serverTimestamp,
    Timestamp,
    collection,
    addDoc,
    onSnapshot,
    deleteDoc,
    updateDoc,
    writeBatch,
    query,
    orderBy
} from "firebase/firestore";
import {
    ref,
    uploadBytes,
    getDownloadURL
} from "firebase/storage";
import { db, storage } from "../../firebase";
import Swal from "sweetalert2";

export default function AdminAdManager({ clubId, THEME_COLOR }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [ads, setAds] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        enabled: false,
        title: "",
        description: "",
        image: "",
        buttonText: "",
        buttonLink: "",
        expiresAt: ""
    });

    /* ================= LOAD ================= */
    useEffect(() => {
        if (!clubId) return;

        const q = collection(db, "clubs", clubId, "ads");

        const unsub = onSnapshot(q, (snap) => {
            const mappedAds = snap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .sort((a, b) => {

                    const orderA =
                        typeof a.order === "number"
                            ? a.order
                            : 9999;

                    const orderB =
                        typeof b.order === "number"
                            ? b.order
                            : 9999;

                    return orderA - orderB;
                });

            setAds(mappedAds);

            setLoading(false);
        });

        return () => unsub();
    }, [clubId]);

    /* ================= IMAGE UPLOAD ================= */
    const handleImageUpload = async (file) => {
        if (!file) return;

        try {
            setUploadingImage(true);

            const extension = file.name.split(".").pop();

            const storageRef = ref(
                storage,
                `clubs/${clubId}/ads/${Date.now()}.${extension}`
            );

            await uploadBytes(storageRef, file);

            const downloadURL = await getDownloadURL(storageRef);

            setForm(prev => ({
                ...prev,
                image: downloadURL
            }));

            Swal.fire({
                icon: "success",
                title: "Banner uploaded",
                timer: 1200,
                showConfirmButton: false
            });

        } catch (err) {
            console.error(err);

            Swal.fire({
                icon: "error",
                title: "Upload failed"
            });
        } finally {
            setUploadingImage(false);
        }
    };

    /* ================= SAVE ================= */
    const saveAd = async () => {
        try {
            setSaving(true);

            if (editingId) {

                await setDoc(
                    doc(db, "clubs", clubId, "ads", editingId),
                    {
                        ...form,
                        expiresAt: form.expiresAt
                            ? Timestamp.fromDate(new Date(form.expiresAt))
                            : null,
                        updatedAt: serverTimestamp()
                    },
                    { merge: true }
                );

            } else {

                await addDoc(
                    collection(db, "clubs", clubId, "ads"),
                    {
                        ...form,
                        order: ads.length,
                        createdAt: serverTimestamp(),
                        expiresAt: form.expiresAt
                            ? Timestamp.fromDate(new Date(form.expiresAt))
                            : null,
                    }
                );
            }

            Swal.fire({
                icon: "success",
                title: "Ad saved",
                timer: 1200,
                showConfirmButton: false
            });

            setEditingId(null);

            setForm({
                enabled: false,
                title: "",
                description: "",
                image: "",
                buttonText: "",
                buttonLink: "",
                expiresAt: ""
            });

        } catch (err) {
            console.error(err);

            Swal.fire({
                icon: "error",
                title: "Failed to save ad"
            });
        } finally {
            setSaving(false);
        }
    };

    const moveAd = async (index, direction) => {

        const newIndex =
            direction === "up"
                ? index - 1
                : index + 1;

        if (
            newIndex < 0 ||
            newIndex >= ads.length
        ) return;

        try {

            const batch = writeBatch(db);

            const currentAd = ads[index];
            const targetAd = ads[newIndex];

            batch.update(
                doc(db, "clubs", clubId, "ads", currentAd.id),
                {
                    order: newIndex
                }
            );

            batch.update(
                doc(db, "clubs", clubId, "ads", targetAd.id),
                {
                    order: index
                }
            );

            await batch.commit();

        } catch (err) {
            console.error(err);

            Swal.fire({
                icon: "error",
                title: "Failed to reorder ads"
            });
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div
                    className="spinner-border"
                    style={{ color: THEME_COLOR }}
                />
            </div>
        );
    }

    return (
        <div
            className="card border-0 shadow-sm rounded-4"
            style={{
                background: "var(--card-bg)"
            }}
        >
            <div className="card-body p-4">
                {/* HEADER */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h4
                            className="fw-bold mb-1"
                            style={{ color: "var(--text-color)" }}
                        >
                            Ad Manager
                        </h4>

                        <div className="small text-muted">
                            Create popup announcements for players
                        </div>
                    </div>

                    <div className="form-check form-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={form.enabled}
                            onChange={(e) =>
                                setForm(prev => ({
                                    ...prev,
                                    enabled: e.target.checked
                                }))
                            }
                            style={{
                                width: 50,
                                height: 26
                            }}
                        />
                    </div>
                </div>

                {/* TITLE */}
                <div className="mb-3">
                    <label className="form-label fw-semibold">
                        Title
                    </label>

                    <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="Tournament Announcement"
                        value={form.title}
                        onChange={(e) =>
                            setForm(prev => ({
                                ...prev,
                                title: e.target.value
                            }))
                        }
                    />
                </div>

                {/* DESCRIPTION */}
                <div className="mb-3">
                    <label className="form-label fw-semibold">
                        Description
                    </label>

                    <textarea
                        className="form-control"
                        rows={4}
                        placeholder="Write announcement..."
                        value={form.description}
                        onChange={(e) =>
                            setForm(prev => ({
                                ...prev,
                                description: e.target.value
                            }))
                        }
                    />
                </div>

                {/* IMAGE */}
                {/* IMAGE */}
                <div className="mb-3">
                    <label className="form-label fw-semibold">
                        Banner Image
                    </label>

                    <div
                        className="border rounded-4 p-4 text-center"
                        style={{
                            background: "var(--surface-bg)",
                            borderStyle: "dashed",
                            cursor: "pointer"
                        }}
                    >
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            id="ad-banner-upload"
                            onChange={(e) =>
                                handleImageUpload(e.target.files?.[0])
                            }
                        />

                        <label
                            htmlFor="ad-banner-upload"
                            style={{
                                cursor: "pointer",
                                width: "100%",
                            }}
                        >
                            {uploadingImage ? (
                                <>
                                    <div
                                        className="spinner-border mb-2"
                                        style={{ color: THEME_COLOR }}
                                    />
                                    <div className="small text-muted">
                                        Uploading banner...
                                    </div>
                                </>
                            ) : (
                                <>
                                    <i
                                        className="bi bi-cloud-arrow-up fs-1"
                                        style={{ color: THEME_COLOR }}
                                    ></i>

                                    <div
                                        className="fw-semibold mt-2"
                                        style={{
                                            color: "var(--text-color)"
                                        }}
                                    >
                                        Upload Banner
                                    </div>

                                    <div className="small text-muted">
                                        PNG, JPG, WEBP
                                    </div>
                                </>
                            )}
                        </label>
                    </div>
                </div>

                {/* IMAGE PREVIEW */}
                {form.image && (
                    <div className="mb-4">
                        <img
                            src={form.image}
                            alt="Preview"
                            className="w-100 rounded-4 border"
                            style={{
                                height: 220,
                                objectFit: "cover"
                            }}
                        />
                    </div>
                )}

                {/* BUTTON */}
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">
                            Button Text
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            placeholder="Register Now"
                            value={form.buttonText}
                            onChange={(e) =>
                                setForm(prev => ({
                                    ...prev,
                                    buttonText: e.target.value
                                }))
                            }
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">
                            Button Link
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            placeholder="https://..."
                            value={form.buttonLink}
                            onChange={(e) =>
                                setForm(prev => ({
                                    ...prev,
                                    buttonLink: e.target.value
                                }))
                            }
                        />
                    </div>
                </div>

                {/* EXPIRY */}
                <div className="mb-4">
                    <label className="form-label fw-semibold">
                        Expiry Date
                    </label>

                    <input
                        type="datetime-local"
                        className="form-control"
                        value={form.expiresAt}
                        onChange={(e) =>
                            setForm(prev => ({
                                ...prev,
                                expiresAt: e.target.value
                            }))
                        }
                    />
                </div>

                {/* SAVE */}
                <button
                    onClick={saveAd}
                    disabled={saving}
                    className="btn btn-lg w-100 text-white fw-bold rounded-pill"
                    style={{
                        background: THEME_COLOR
                    }}
                >
                    {saving ? "Saving..." : "Save Advertisement"}
                </button>
                {/* ADS LIST */}
                <div className="mt-5">
                    <h5 className="fw-bold mb-3">
                        Existing Ads
                    </h5>

                    <div className="d-flex flex-column gap-3">

                        {ads.map((ad, index) => (
                            <div
                                key={ad.id}
                                className="border rounded-4 p-3"
                                style={{
                                    background: "var(--surface-bg)"
                                }}
                            >
                                <div className="d-flex gap-3">

                                    <img
                                        src={ad.image}
                                        alt=""
                                        style={{
                                            width: 90,
                                            height: 90,
                                            objectFit: "cover",
                                            borderRadius: 16
                                        }}
                                    />

                                    <div className="flex-grow-1">
                                        <div className="fw-bold">
                                            {ad.title}
                                        </div>

                                        <div className="small text-muted mb-2">
                                            {ad.description}
                                        </div>

                                        <span
                                            className={`badge ${ad.enabled
                                                ? "bg-success"
                                                : "bg-secondary"
                                                }`}
                                        >
                                            {ad.enabled ? "Active" : "Disabled"}
                                        </span>
                                    </div>

                                    <div className="d-flex flex-column gap-2">

                                        <button
                                            className="btn btn-sm btn-light"
                                            disabled={index === 0}
                                            onClick={() => moveAd(index, "up")}
                                        >
                                            <i className="bi bi-arrow-up"></i>
                                        </button>

                                        <button
                                            className="btn btn-sm btn-light"
                                            disabled={index === ads.length - 1}
                                            onClick={() => moveAd(index, "down")}
                                        >
                                            <i className="bi bi-arrow-down"></i>
                                        </button>

                                        <button
                                            className="btn btn-sm btn-light"
                                            onClick={() => {
                                                setEditingId(ad.id);

                                                setForm({
                                                    enabled: ad.enabled || false,
                                                    title: ad.title || "",
                                                    description: ad.description || "",
                                                    image: ad.image || "",
                                                    buttonText: ad.buttonText || "",
                                                    buttonLink: ad.buttonLink || "",
                                                    expiresAt: ad.expiresAt
                                                        ? new Date(ad.expiresAt.toMillis())
                                                            .toISOString()
                                                            .slice(0, 16)
                                                        : ""
                                                });
                                            }}
                                        >
                                            <i className="bi bi-pencil"></i>
                                        </button>

                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={async () => {

                                                const confirm =
                                                    await Swal.fire({
                                                        title: "Delete ad?",
                                                        icon: "warning",
                                                        showCancelButton: true,
                                                    });

                                                if (!confirm.isConfirmed) return;

                                                await deleteDoc(
                                                    doc(
                                                        db,
                                                        "clubs",
                                                        clubId,
                                                        "ads",
                                                        ad.id
                                                    )
                                                );
                                            }}
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>

                                    </div>
                                </div>
                            </div>
                        ))}

                    </div>
                </div>
            </div>
        </div>
    );
}